// src/render/hud-layout.ts
// Shared button layout in game coordinates (1600x900 desktop-first).
// Used by both draw.ts (rendering) and controls.ts (hit testing).

import { GAME_W, GAME_H } from './canvas';

export const HUD_H = 110;            // total HUD height
export const HUD_Y = GAME_H - HUD_H; // top of HUD strip

const ROW_H = 44;
const BTN_H = 32;
const BTN_W_BIG = 64;
const BTN_W_SM = 52;
const PAD = 12;

export const ROW1_Y = HUD_Y + 8;     // angle row center-y area
export const ROW2_Y = HUD_Y + 8 + ROW_H; // power row center-y area

// FIRE button
export const FIRE_W = 140;
export const FIRE_X = GAME_W - FIRE_W - PAD;
export const FIRE_Y = HUD_Y + 8;
export const FIRE_H = HUD_H - 16;

// Left-side buttons per row: [◄◄] [◄] ... [►] [►►]
export interface Btn { x: number; y: number; w: number; h: number; }

function rowBtns(rowY: number): { decBig: Btn; decSm: Btn; incSm: Btn; incBig: Btn } {
  const y = rowY;
  const rightEdge = FIRE_X - PAD;
  return {
    decBig: { x: PAD, y, w: BTN_W_BIG, h: BTN_H },
    decSm:  { x: PAD + BTN_W_BIG + 4, y, w: BTN_W_SM, h: BTN_H },
    incSm:  { x: rightEdge - BTN_W_BIG - 4 - BTN_W_SM, y, w: BTN_W_SM, h: BTN_H },
    incBig: { x: rightEdge - BTN_W_BIG, y, w: BTN_W_BIG, h: BTN_H },
  };
}

export const angleBtns = rowBtns(ROW1_Y);
export const powerBtns = rowBtns(ROW2_Y);

// Power row gets an extra ±100 tier to the outside of ±10
const BTN_W_XL = 44;
export const powerBigBtns: { decHuge: Btn; incHuge: Btn } = {
  decHuge: { x: PAD + BTN_W_BIG + 4 + BTN_W_SM + 4, y: ROW2_Y, w: BTN_W_XL, h: BTN_H },
  incHuge: { x: FIRE_X - PAD - BTN_W_BIG - 4 - BTN_W_SM - 4 - BTN_W_XL, y: ROW2_Y, w: BTN_W_XL, h: BTN_H },
};

// Value label positions (centered between the inner buttons)
export const ANGLE_LABEL_X = GAME_W / 2 - FIRE_W / 2;
export const ANGLE_LABEL_Y = ROW1_Y + BTN_H / 2 + 5;
export const POWER_LABEL_X = GAME_W / 2 - FIRE_W / 2;
export const POWER_LABEL_Y = ROW2_Y + BTN_H / 2 + 5;

// Gear button (top-right of game area, above HUD)
export const GEAR_BTN = { x: GAME_W - 48, y: 6, w: 38, h: 38 };

// Weapon picker constants
export const WEAPON_NAME_BTN: Btn = { x: PAD, y: HUD_Y - 32, w: FIRE_X - PAD * 2, h: 28 };
export const WEAPON_PICKER_H = 480;
export const WEAPON_PICKER: Btn = { x: PAD, y: HUD_Y - 32 - WEAPON_PICKER_H, w: FIRE_X - PAD * 2, h: WEAPON_PICKER_H };
export const WEAPON_ROW_H = 48;

export function hitTest(gx: number, gy: number, pickerOpen = false): string | null {
  // Check gear icon
  if (gx >= GEAR_BTN.x && gx <= GEAR_BTN.x + GEAR_BTN.w &&
      gy >= GEAR_BTN.y && gy <= GEAR_BTN.y + GEAR_BTN.h) {
    return 'gear';
  }
  // Weapon name button (tap to toggle picker)
  if (inBtn(gx, gy, WEAPON_NAME_BTN)) {
    return 'weapon-toggle';
  }
  // When picker is open, check taps inside picker area
  if (pickerOpen && inBtn(gx, gy, WEAPON_PICKER)) {
    const rowIdx = Math.floor((gy - WEAPON_PICKER.y) / WEAPON_ROW_H);
    return `weapon-pick:${rowIdx}`;
  }
  // If picker is open, block other interactions
  if (pickerOpen) return null;
  // Check FIRE first
  if (gx >= FIRE_X && gx <= FIRE_X + FIRE_W && gy >= FIRE_Y && gy <= FIRE_Y + FIRE_H) {
    return 'fire';
  }
  // Check angle row buttons
  if (inBtn(gx, gy, angleBtns.decBig)) return 'angle-5';
  if (inBtn(gx, gy, angleBtns.decSm))  return 'angle-1';
  if (inBtn(gx, gy, angleBtns.incSm))  return 'angle+1';
  if (inBtn(gx, gy, angleBtns.incBig)) return 'angle+5';
  // Check power row buttons
  if (inBtn(gx, gy, powerBigBtns.decHuge)) return 'power-100';
  if (inBtn(gx, gy, powerBigBtns.incHuge)) return 'power+100';
  if (inBtn(gx, gy, powerBtns.decBig)) return 'power-10';
  if (inBtn(gx, gy, powerBtns.decSm))  return 'power-1';
  if (inBtn(gx, gy, powerBtns.incSm))  return 'power+1';
  if (inBtn(gx, gy, powerBtns.incBig)) return 'power+10';
  return null;
}

function inBtn(gx: number, gy: number, b: Btn): boolean {
  return gx >= b.x && gx <= b.x + b.w && gy >= b.y && gy <= b.y + b.h;
}
